import {
  formatRevisionEtag,
  type AcceptInvitationRequest,
  type ApiErrorBody,
  type CalendarApplyRequest,
  type CalendarCatalogResponse,
  type CalendarDiffRequest,
  type CalendarUpgradeDiffResponse,
  type CalendarUpgradeRequest,
  type ChangeMemberRoleRequest,
  type CollaborationSummary,
  type CreateInvitationRequest,
  type CreateProjectRequest,
  type CsrfTokenResponse,
  type ImportProjectRequest,
  type MeResponse,
  type ProjectDocument,
  type ProjectInvitation,
  type ProjectListItem,
  type ProjectMember,
  type ProjectRevisionSummary,
  type ProjectViewDocument,
  type RestoreProjectRevisionRequest,
  type SaveProjectRequest,
  type SaveProjectViewRequest,
  type TaskAttachment,
  type TaskComment,
  type TransferOwnerRequest,
  type UpdateProjectMetadataRequest,
} from '@bigint-project/cloud-contracts';

export interface Etagged<T> {
  readonly data: T;
  readonly etag: string;
  /** Present on combined Project responses; independent from the Project ETag. */
  readonly viewEtag?: string;
}

interface RequestOptions {
  readonly mutation?: boolean;
  readonly etag?: string;
  readonly clientOperationId?: string;
}

type CollectionEnvelope<T> =
  | readonly T[]
  | { readonly items: readonly T[] }
  | Readonly<Record<string, readonly T[]>>;

export interface CreatedInvitationResult {
  readonly invitation: ProjectInvitation;
  readonly token: string | null;
  readonly idempotent: boolean;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: ApiErrorBody['error']['details'];

  constructor(status: number, code: string, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = body?.error.requestId;
    this.details = body?.error.details;
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const error = (value as { readonly error?: unknown }).error;
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { readonly code?: unknown }).code === 'string' &&
    typeof (error as { readonly message?: unknown }).message === 'string'
  );
}

function extractCollection<T>(value: CollectionEnvelope<T>, key: string): readonly T[] {
  if (Array.isArray(value)) return value;
  const record = value as Readonly<Record<string, unknown>>;
  const candidate = record[key] ?? record.items;
  if (!Array.isArray(candidate)) throw new Error(`API response is missing ${key}`);
  return candidate as readonly T[];
}

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function operationId(): string {
  return globalThis.crypto.randomUUID();
}

export interface CloudApi {
  readonly loginUrl: (returnTo: string) => string;
  readonly getMe: () => Promise<MeResponse>;
  readonly logout: () => Promise<void>;
  readonly listProjects: () => Promise<readonly ProjectListItem[]>;
  readonly createProject: (name: string) => Promise<ProjectDocument>;
  readonly importProject: (file: unknown) => Promise<ProjectDocument>;
  readonly getProject: (id: string) => Promise<Etagged<ProjectDocument>>;
  readonly saveProject: (id: string, request: SaveProjectRequest, etag: string) => Promise<Etagged<ProjectDocument>>;
  readonly getProjectView: (id: string) => Promise<Etagged<ProjectViewDocument>>;
  readonly saveProjectView: (id: string, request: SaveProjectViewRequest, etag: string) => Promise<string>;
  readonly updateProjectMetadata: (
    id: string,
    request: Omit<UpdateProjectMetadataRequest, 'clientOperationId'>,
    etag: string,
  ) => Promise<ProjectDocument>;
  readonly listRevisions: (id: string) => Promise<readonly ProjectRevisionSummary[]>;
  readonly restoreRevision: (id: string, revision: number, etag: string) => Promise<Etagged<ProjectDocument>>;
  readonly listMembers: (id: string) => Promise<readonly ProjectMember[]>;
  readonly listInvitations: (id: string) => Promise<readonly ProjectInvitation[]>;
  readonly createInvitation: (
    id: string,
    request: Omit<CreateInvitationRequest, 'clientOperationId'>,
  ) => Promise<CreatedInvitationResult>;
  readonly revokeInvitation: (id: string, invitationId: string) => Promise<void>;
  readonly acceptInvitation: (token: string) => Promise<{ readonly projectId: string }>;
  readonly changeMemberRole: (
    id: string,
    userId: string,
    request: Omit<ChangeMemberRoleRequest, 'clientOperationId'>,
  ) => Promise<void>;
  readonly removeMember: (id: string, userId: string) => Promise<void>;
  readonly transferOwner: (
    id: string,
    request: Omit<TransferOwnerRequest, 'clientOperationId'>,
  ) => Promise<void>;
  readonly getCalendarCatalog: () => Promise<CalendarCatalogResponse>;
  readonly diffCalendar: (id: string, request: CalendarDiffRequest) => Promise<CalendarUpgradeDiffResponse>;
  readonly applyCalendar: (
    id: string,
    request: Omit<CalendarApplyRequest, 'clientOperationId'>,
    etag: string,
  ) => Promise<Etagged<ProjectDocument>>;
  readonly upgradeCalendar: (
    id: string,
    request: Omit<CalendarUpgradeRequest, 'clientOperationId'>,
    etag: string,
  ) => Promise<Etagged<ProjectDocument>>;
  readonly getCollaborationSummary: (id: string) => Promise<CollaborationSummary>;
  readonly listTaskComments: (id: string, taskId: string) => Promise<readonly TaskComment[]>;
  readonly createTaskComment: (id: string, taskId: string, body: string) => Promise<TaskComment>;
  readonly deleteTaskComment: (id: string, taskId: string, commentId: string) => Promise<void>;
  readonly listTaskAttachments: (id: string, taskId: string) => Promise<readonly TaskAttachment[]>;
  readonly uploadTaskAttachment: (id: string, taskId: string, file: File) => Promise<TaskAttachment>;
  readonly taskAttachmentDownloadUrl: (id: string, taskId: string, attachmentId: string) => string;
  readonly deleteTaskAttachment: (id: string, taskId: string, attachmentId: string) => Promise<void>;
}

export class CloudApiClient implements CloudApi {
  readonly #baseUrl: string;
  #csrfToken: string | null = null;
  #csrfPromise: Promise<string> | null = null;

  constructor(baseUrl = '') {
    this.#baseUrl = baseUrl.replace(/\/$/, '');
  }

  loginUrl(returnTo: string): string {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const url = new URL(`${this.#baseUrl}/api/v1/auth/login`, origin);
    url.searchParams.set('return_to', returnTo);
    return url.toString();
  }

  async getMe(): Promise<MeResponse> {
    const response = await this.#request<MeResponse>('/api/v1/me');
    const token = (response as MeResponse & { readonly csrfToken?: string }).csrfToken;
    if (typeof token === 'string' && token.length > 0) this.#csrfToken = token;
    return response;
  }

  logout(): Promise<void> {
    return this.#request('/api/v1/auth/logout', { method: 'POST' }, { mutation: true });
  }

  async listProjects(): Promise<readonly ProjectListItem[]> {
    const response = await this.#request<CollectionEnvelope<ProjectListItem>>('/api/v1/projects');
    return extractCollection(response, 'projects');
  }

  async createProject(name: string): Promise<ProjectDocument> {
    const request: CreateProjectRequest = { name, clientOperationId: operationId() };
    return this.#request('/api/v1/projects', { method: 'POST', body: JSON.stringify(request) }, { mutation: true });
  }

  async importProject(file: unknown): Promise<ProjectDocument> {
    const request: ImportProjectRequest = { file, clientOperationId: operationId() };
    return this.#request('/api/v1/projects/import', { method: 'POST', body: JSON.stringify(request) }, { mutation: true });
  }

  getProject(id: string): Promise<Etagged<ProjectDocument>> {
    return this.#requestProjectEtagged(`/api/v1/projects/${encodePathSegment(id)}`);
  }

  saveProject(id: string, request: SaveProjectRequest, etag: string): Promise<Etagged<ProjectDocument>> {
    return this.#requestProjectEtagged(
      `/api/v1/projects/${encodePathSegment(id)}`,
      { method: 'PUT', body: JSON.stringify(request) },
      { mutation: true, etag },
    );
  }

  getProjectView(id: string): Promise<Etagged<ProjectViewDocument>> {
    return this.#requestEtagged(`/api/v1/projects/${encodePathSegment(id)}/view`);
  }

  saveProjectView(id: string, request: SaveProjectViewRequest, etag: string): Promise<string> {
    return this.#requestEtagOnly(
      `/api/v1/projects/${encodePathSegment(id)}/view`,
      { method: 'PUT', body: JSON.stringify(request) },
      { mutation: true, etag },
    );
  }

  updateProjectMetadata(
    id: string,
    request: Omit<UpdateProjectMetadataRequest, 'clientOperationId'>,
    etag: string,
  ): Promise<ProjectDocument> {
    return this.#request(
      `/api/v1/projects/${encodePathSegment(id)}/metadata`,
      { method: 'PATCH', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true, etag },
    );
  }

  async listRevisions(id: string): Promise<readonly ProjectRevisionSummary[]> {
    const response = await this.#request<CollectionEnvelope<ProjectRevisionSummary>>(
      `/api/v1/projects/${encodePathSegment(id)}/revisions`,
    );
    return extractCollection(response, 'revisions');
  }

  restoreRevision(id: string, revision: number, etag: string): Promise<Etagged<ProjectDocument>> {
    const request: RestoreProjectRevisionRequest = { clientOperationId: operationId() };
    return this.#requestProjectEtagged(
      `/api/v1/projects/${encodePathSegment(id)}/revisions/${revision}:restore`,
      { method: 'POST', body: JSON.stringify(request) },
      { mutation: true, etag },
    );
  }

  async listMembers(id: string): Promise<readonly ProjectMember[]> {
    const response = await this.#request<CollectionEnvelope<ProjectMember>>(
      `/api/v1/projects/${encodePathSegment(id)}/members`,
    );
    return extractCollection(response, 'members');
  }

  async listInvitations(id: string): Promise<readonly ProjectInvitation[]> {
    const response = await this.#request<CollectionEnvelope<ProjectInvitation>>(
      `/api/v1/projects/${encodePathSegment(id)}/members`,
    );
    return extractCollection(response, 'invitations');
  }

  createInvitation(
    id: string,
    request: Omit<CreateInvitationRequest, 'clientOperationId'>,
  ): Promise<CreatedInvitationResult> {
    return this.#request<CreatedInvitationResult>(
      `/api/v1/projects/${encodePathSegment(id)}/invitations`,
      { method: 'POST', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true },
    );
  }

  revokeInvitation(id: string, invitationId: string): Promise<void> {
    const clientOperationId = operationId();
    return this.#request(
      `/api/v1/projects/${encodePathSegment(id)}/invitations/${encodePathSegment(invitationId)}`,
      { method: 'DELETE' },
      { mutation: true, clientOperationId },
    );
  }

  acceptInvitation(token: string): Promise<{ readonly projectId: string }> {
    const request: AcceptInvitationRequest = { clientOperationId: operationId() };
    return this.#request(
      `/api/v1/invitations/${encodePathSegment(token)}:accept`,
      { method: 'POST', body: JSON.stringify(request) },
      { mutation: true },
    );
  }

  changeMemberRole(
    id: string,
    userId: string,
    request: Omit<ChangeMemberRoleRequest, 'clientOperationId'>,
  ): Promise<void> {
    return this.#request(
      `/api/v1/projects/${encodePathSegment(id)}/members/${encodePathSegment(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true },
    );
  }

  removeMember(id: string, userId: string): Promise<void> {
    const clientOperationId = operationId();
    return this.#request(
      `/api/v1/projects/${encodePathSegment(id)}/members/${encodePathSegment(userId)}`,
      { method: 'DELETE' },
      { mutation: true, clientOperationId },
    );
  }

  transferOwner(
    id: string,
    request: Omit<TransferOwnerRequest, 'clientOperationId'>,
  ): Promise<void> {
    return this.#request(
      `/api/v1/projects/${encodePathSegment(id)}:transfer-owner`,
      { method: 'POST', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true },
    );
  }

  getCalendarCatalog(): Promise<CalendarCatalogResponse> {
    return this.#request('/api/v1/calendar-catalog');
  }

  diffCalendar(id: string, request: CalendarDiffRequest): Promise<CalendarUpgradeDiffResponse> {
    const projectId = encodePathSegment(id);
    const calendarId = encodePathSegment(request.projectCalendarId);
    const query = new URLSearchParams({ calendarReleaseId: request.calendarReleaseId });
    return this.#request(`/api/v1/projects/${projectId}/calendars/${calendarId}/diff?${query.toString()}`);
  }

  applyCalendar(
    id: string,
    request: Omit<CalendarApplyRequest, 'clientOperationId'>,
    etag: string,
  ): Promise<Etagged<ProjectDocument>> {
    const projectId = encodePathSegment(id);
    const calendarId = encodePathSegment(request.projectCalendarId);
    return this.#requestProjectEtagged(
      `/api/v1/projects/${projectId}/calendars/${calendarId}:apply`,
      { method: 'POST', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true, etag },
    );
  }

  upgradeCalendar(
    id: string,
    request: Omit<CalendarUpgradeRequest, 'clientOperationId'>,
    etag: string,
  ): Promise<Etagged<ProjectDocument>> {
    const projectId = encodePathSegment(id);
    const calendarId = encodePathSegment(request.projectCalendarId);
    return this.#requestProjectEtagged(
      `/api/v1/projects/${projectId}/calendars/${calendarId}:upgrade`,
      { method: 'POST', body: JSON.stringify({ ...request, clientOperationId: operationId() }) },
      { mutation: true, etag },
    );
  }

  getCollaborationSummary(id: string): Promise<CollaborationSummary> {
    return this.#request(`${this.#collaborationPath(id)}/summary`);
  }

  async listTaskComments(id: string, taskId: string): Promise<readonly TaskComment[]> {
    const response = await this.#request<CollectionEnvelope<TaskComment>>(
      `${this.#taskPath(id, taskId)}/comments`,
    );
    return extractCollection(response, 'comments');
  }

  createTaskComment(id: string, taskId: string, body: string): Promise<TaskComment> {
    return this.#request(
      `${this.#taskPath(id, taskId)}/comments`,
      { method: 'POST', body: JSON.stringify({ body }) },
      { mutation: true },
    );
  }

  deleteTaskComment(id: string, taskId: string, commentId: string): Promise<void> {
    return this.#request(
      `${this.#taskPath(id, taskId)}/comments/${encodePathSegment(commentId)}`,
      { method: 'DELETE' },
      { mutation: true, clientOperationId: operationId() },
    );
  }

  async listTaskAttachments(id: string, taskId: string): Promise<readonly TaskAttachment[]> {
    const response = await this.#request<CollectionEnvelope<TaskAttachment>>(
      `${this.#taskPath(id, taskId)}/attachments`,
    );
    return extractCollection(response, 'attachments');
  }

  uploadTaskAttachment(id: string, taskId: string, file: File): Promise<TaskAttachment> {
    // 上傳走 raw body：檔名以 query 傳遞，Content-Type 由瀏覽器依 File 型別帶出。
    return this.#request(
      `${this.#taskPath(id, taskId)}/attachments?fileName=${encodeURIComponent(file.name)}`,
      { method: 'POST', body: file },
      { mutation: true },
    );
  }

  taskAttachmentDownloadUrl(id: string, taskId: string, attachmentId: string): string {
    return `${this.#baseUrl}${this.#taskPath(id, taskId)}/attachments/${encodePathSegment(attachmentId)}/content`;
  }

  deleteTaskAttachment(id: string, taskId: string, attachmentId: string): Promise<void> {
    return this.#request(
      `${this.#taskPath(id, taskId)}/attachments/${encodePathSegment(attachmentId)}`,
      { method: 'DELETE' },
      { mutation: true, clientOperationId: operationId() },
    );
  }

  #collaborationPath(id: string): string {
    return `/api/v1/projects/${encodePathSegment(id)}/collaboration`;
  }

  #taskPath(id: string, taskId: string): string {
    return `${this.#collaborationPath(id)}/tasks/${encodePathSegment(taskId)}`;
  }

  async #ensureCsrfToken(): Promise<string> {
    if (this.#csrfToken !== null) return this.#csrfToken;
    if (this.#csrfPromise !== null) return this.#csrfPromise;
    this.#csrfPromise = this.#request<CsrfTokenResponse>('/api/v1/auth/csrf').then((response) => {
      if (typeof response.token !== 'string' || response.token.length === 0) {
        throw new Error('CSRF endpoint returned an empty token');
      }
      this.#csrfToken = response.token;
      return response.token;
    }).finally(() => {
      this.#csrfPromise = null;
    });
    return this.#csrfPromise;
  }

  async #requestEtagged<T>(
    path: string,
    init: RequestInit = {},
    options: RequestOptions = {},
  ): Promise<Etagged<T>> {
    const response = await this.#fetch(path, init, options);
    const data = await this.#readJson<T>(response);
    const fallbackRevision = (data as { readonly serverRevision?: unknown }).serverRevision;
    const etag = response.headers.get('ETag') ??
      (typeof fallbackRevision === 'number' ? formatRevisionEtag(fallbackRevision) : null);
    if (etag === null) throw new Error('Project response is missing ETag');
    return { data, etag };
  }

  async #requestProjectEtagged(
    path: string,
    init: RequestInit = {},
    options: RequestOptions = {},
  ): Promise<Etagged<ProjectDocument>> {
    const response = await this.#fetch(path, init, options);
    const data = await this.#readJson<ProjectDocument>(response);
    const etag = response.headers.get('X-Project-ETag') ??
      response.headers.get('ETag') ??
      formatRevisionEtag(data.serverRevision);
    const viewEtag = response.headers.get('X-View-ETag') ?? formatRevisionEtag(data.view.revision);
    return { data, etag, viewEtag };
  }

  async #requestEtagOnly(
    path: string,
    init: RequestInit,
    options: RequestOptions,
  ): Promise<string> {
    const response = await this.#fetch(path, init, options);
    const etag = response.headers.get('ETag');
    if (etag === null) throw new Error('View response is missing ETag');
    return etag;
  }

  async #request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
    const response = await this.#fetch(path, init, options);
    if (response.status === 204) return undefined as T;
    return this.#readJson<T>(response);
  }

  async #fetch(path: string, init: RequestInit, options: RequestOptions): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    // 只有 JSON 字串 body 才標成 JSON；附件上傳送的是 File，Content-Type 交給瀏覽器。
    if (typeof init.body === 'string') headers.set('Content-Type', 'application/json');
    if (options.etag !== undefined) headers.set('If-Match', options.etag);
    if (options.clientOperationId !== undefined) headers.set('X-Client-Operation-Id', options.clientOperationId);
    if (options.mutation === true) headers.set('X-CSRF-Token', await this.#ensureCsrfToken());

    const response = await fetch(`${this.#baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });
    if (response.ok) return response;

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    if (isApiErrorBody(body)) {
      if (response.status === 403 && body.error.code.startsWith('CSRF_')) this.#csrfToken = null;
      throw new ApiError(response.status, body.error.code, body.error.message, body);
    }
    throw new ApiError(response.status, `HTTP_${response.status}`, `Request failed with HTTP ${response.status}`);
  }

  async #readJson<T>(response: Response): Promise<T> {
    try {
      return (await response.json()) as T;
    } catch {
      throw new Error('API returned invalid JSON');
    }
  }
}

export function createCloudApiClient(): CloudApi {
  return new CloudApiClient(import.meta.env.VITE_API_BASE_URL ?? '');
}

export function createClientOperationId(): string {
  return operationId();
}

export function isConflictError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 409 || error.status === 412);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const base = `${error.message}（${error.code}）`;
    const details = (error.details ?? [])
      .map((detail) => (detail.path === undefined ? detail.reason : `${detail.path}：${detail.reason}`))
      .join('；');
    return details.length === 0 ? base : `${base}：${details}`;
  }
  return error instanceof Error ? error.message : '發生未預期的錯誤';
}
